import { ReactNode } from 'react';
import DynamicIcon, { DynamicIconProps } from 'src/shared/components/ui/icons/dynamic-icon';
import { cn } from 'src/shared/lib/utils';

export type EmptyStateCardProps = {
  title: string;
  description: ReactNode;
  children?: ReactNode;
  border?: boolean;
  icon?: DynamicIconProps['name'];
  height?: number;
  size?: 'sm' | 'md';
  layout?: 'horizontal' | 'vertical';
  retryText?: string;
  className?: string;
  retry?: () => void;
  testId?: string;
};

export default function EmptyStateCard({
  description,
  title,
  border,
  icon,
  size = 'md',
  height,
  layout = 'horizontal',
  children,
  retryText,
  retry,
  className,
  testId,
}: EmptyStateCardProps) {
  const isVertical = layout === 'vertical';

  return (
    <div
      data-testid={testId}
      className={cn(
        'p-5 flex w-full',
        border && 'border rounded-sm',
        isVertical ? 'flex-col items-start justify-center gap-4 py-10 px-4' : 'items-center justify-center gap-5',
        className
      )}
      style={{ height: height ? `${height}px` : size === 'sm' ? '120px' : isVertical ? undefined : '160px' }}
    >
      <div className={cn(isVertical ? 'flex justify-start shrink-0' : 'flex items-center pr-5')}>
        <DynamicIcon
          name={icon || 'CircleOff'}
          size={size === 'sm' ? 25 : 35}
          className={cn('text-typography-grey', size === 'md' ? 'stroke-[1.25px]' : 'stroke-[1.5px]')}
        />
      </div>
      <div className={cn('pb-1', isVertical ? 'flex flex-col items-start w-full max-w-[320px]' : 'max-w-[320px] grow')}>
        <p
          className={cn(
            'mb-0.5',
            size === 'sm' ? 'text-sm' : 'text-base',
            isVertical && 'font-semibold text-typography-black'
          )}
        >
          {title}
        </p>
        <p
          className={cn(
            'text-typography-grey leading-snug py-0.5',
            size === 'sm' ? 'text-xs' : 'text-sm',
            'leading-tight'
          )}
        >
          {description}
        </p>
        {children && <div className={cn(isVertical && 'mt-3 flex justify-start w-full')}>{children}</div>}
        {retry && (
          <span
            className={cn(
              'select-none text-typography-grey underline cursor-pointer hover:text-typography-black transition-all inline-flex items-center gap-1',
              size === 'sm' ? 'text-xs' : 'text-sm'
            )}
            onClick={retry}
          >
            {retryText || 'Try again'}
          </span>
        )}
      </div>
    </div>
  );
}
